import React, { useState } from "react";
import Calendar from "react-calendar";
import {
  UserDashboad,
  UserDashboadCalendar,
  envelopeLeft,
} from "../../../services/CommonService";
import { useNavigate } from "react-router-dom";
import {
  DocumentIcon,
  BuildingIcon,
  UsersIcon,
  TemplatesIcon,
  ImportIcon,
  PlusIcon,
  OpenFolder,
  DraftStatusIcon,
  PreviewStatusIcon,
  ApprovedStatusIcon,
  SendStatusIcon,
  CompletedStatusIcon,
} from "./DashbordIcon";
import { useQuery } from "@tanstack/react-query";

import "react-calendar/dist/Calendar.css";
import "./Dashboard.css";

const Dashboard = () => {
  const navigate = useNavigate();

  // Local UI state only
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [eventFilter, setEventFilter] = useState("upcoming");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    error: dashboardError,
  } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await UserDashboad();
      return res.data;
    },
  });

  const {
    data: calendarEvents = [],
    isLoading: isCalendarLoading,
    error: calendarError,
  } = useQuery({
    queryKey: ["calendar-events"],
    queryFn: async () => {
      const res = await UserDashboadCalendar();
      return res.data.calendarEvents || [];
    },
  });

  const {
    data: totalEnvelope = 0,
    isLoading: isEnvelopeLoading,
    error: envelopeError,
  } = useQuery({
    queryKey: ["envelope-left"],
    queryFn: async () => {
      const res = await envelopeLeft({});
      return res.data.data.total_envelope;
    },
  });

  const getDaysDifference = (eventDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const event = new Date(eventDate);
    event.setHours(0, 0, 0, 0);
    return Math.floor((event - today) / (1000 * 60 * 60 * 24));
  };

  const getFilteredEvents = () => {
    const selectedDateStr = selectedDate.toISOString().split("T")[0];
    let events = [];

    switch (eventFilter) {
      case "selectedDate":
        events = calendarEvents.filter(
          (event) => event.date === selectedDateStr
        );
        break;

      case "nextWeek":
        events = calendarEvents.filter((event) => {
          const daysDiff = getDaysDifference(event.date);
          return daysDiff > 0 && daysDiff <= 7;
        });
        break;

      case "nextMonth":
        events = calendarEvents.filter((event) => {
          const daysDiff = getDaysDifference(event.date);
          return daysDiff > 0 && daysDiff <= 30;
        });
        break;

      case "past":
        events = calendarEvents.filter((event) => {
          const daysDiff = getDaysDifference(event.date);
          return daysDiff < 0;
        });
        break;

      case "upcoming":
      default:
        events = calendarEvents.filter((event) => {
          const daysDiff = getDaysDifference(event.date);
          return daysDiff >= 0;
        });
        break;
    }

    return events;
  };

  if (isDashboardLoading || isCalendarLoading || isEnvelopeLoading) {
    return (
      <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>
    );
  }

  if (dashboardError) {
    console.error("Failed to fetch dashboard data:", dashboardError);
  }
  if (calendarError) {
    console.error("Failed to fetch calendar data:", calendarError);
  }
  if (envelopeError) {
    console.error("Failed to fetch envelope data:", envelopeError);
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-loading">
        <p>No data found.</p>
      </div>
    );
  }

  const { summary, recentActivity, negotiationByStatus } = dashboardData;

  const statusData = [
    { key: "draft", label: "Draft", color: "#8b5cf6", icon: DraftStatusIcon },
    { key: "underPreview", label: "Under Preview", color: "#d946ef", icon: PreviewStatusIcon },
    { key: "approved", label: "Approved", color: "#ec4899", icon: ApprovedStatusIcon },
    { key: "send", label: "Send", color: "#f97316", icon: SendStatusIcon },
    { key: "completed", label: "Completed", color: "#fb923c", icon: CompletedStatusIcon },
  ];

  const totalContracts = negotiationByStatus
    ? Object.values(negotiationByStatus).reduce((sum, val) => sum + val, 0)
    : 0;

  const calendarEventDates =
    calendarEvents?.map((event) => event.date) || [];

  const getTileClassName = ({ date, view }) => {
    if (view === "month") {
      const dateString = date.toISOString().split("T")[0];
      const isToday =
        new Date().toISOString().split("T")[0] === dateString;
      const isSelected =
        selectedDate.toISOString().split("T")[0] === dateString;
      const hasEvent = calendarEventDates.includes(dateString);

      if (isSelected) {
        return "react-calendar__tile--selected";
      } else if (isToday) {
        return "react-calendar__tile--today";
      } else if (hasEvent) {
        return "react-calendar__tile--has-events";
      }
    }
    return "";
  };

  const selectedDateEvents = getFilteredEvents();

  const formatEventDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getFilterLabel = () => {
    switch (eventFilter) {
      case "selectedDate":
        return formatEventDate(selectedDate);
      case "nextWeek":
        return "Next Week Events";
      case "nextMonth":
        return "Next Month Events";
      case "past":
        return "Past Events";
      case "upcoming":
      default:
        return "All Upcoming Events";
    }
  };

  const filterOptions = [
    { value: "upcoming", label: "Upcoming Events" },
    { value: "nextWeek", label: "Next Week Events" },
    { value: "nextMonth", label: "Next Month Events" },
    { value: "past", label: "Past Events" },
    { value: "selectedDate", label: "Selected Date" },
  ];

  function onContractStart(e) {
    e.preventDefault();
    navigate("/client-portal/contract");
  }

  const handleUseTemplate = (e) => {
    e.preventDefault();
    navigate("/templates");
  };

  const handleCollabroter = (e) => {
    e.preventDefault();
    navigate("/recipients");
  };

  const handlePricingPlane = (e) => {
    e.preventDefault();
    navigate("/billing/pricing");
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setEventFilter("selectedDate");
  };

  const handleContract = (e) => {
    e.preventDefault();
    navigate("/manage");
  };

  const handleNegotiation = (e) => {
    e.preventDefault();
    navigate("/manage/negotiation");
  };

  const handleCollaborator = (e) => {
    e.preventDefault();
    navigate("/recipients");
  };

  return (
    <div className="dashboard section-bg-color">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <h1 className="dashboard-title">Dashboard</h1>
        </div>
      </div>

      <div className="dashboard-stats">
        <div className="dashboard-stat-card" onClick={handleContract}>
          <div className="stat-card-content">
            <div className="stat-card-info">
              <h2 className="stat-card-value">{summary.totalContracts}</h2>
              <p className="stat-card-label">Total Contracts</p>
            </div>
            <div className="stat-card-icon stat-card-icon-purple">
              <DocumentIcon width={24} height={24} />
            </div>
          </div>
        </div>

        <div className="dashboard-stat-card" onClick={handleNegotiation}>
          <div className="stat-card-content">
            <div className="stat-card-info">
              <h2 className="stat-card-value">
                {summary.activeNegotiations}
              </h2>
              <p className="stat-card-label">Active Negotiations</p>
            </div>
            <div className="stat-card-icon stat-card-icon-purple">
              <BuildingIcon width={24} height={24} />
            </div>
          </div>
        </div>

        <div className="dashboard-stat-card" onClick={handleContract}>
          <div className="stat-card-content">
            <div className="stat-card-info">
              <h2 className="stat-card-value">
                {summary.inProgressSleeves}
              </h2>
              <p className="stat-card-label">In Progress Contract</p>
            </div>
            <div className="stat-card-icon stat-card-icon-purple">
              <OpenFolder width={24} height={24} />
            </div>
          </div>
        </div>

        <div className="dashboard-stat-card" onClick={handleCollaborator}>
          <div className="stat-card-content">
            <div className="stat-card-info">
              <h2 className="stat-card-value">
                {summary.totalCollaborators}
              </h2>
              <p className="stat-card-label">Collaborators</p>
            </div>
            <div className="stat-card-icon stat-card-icon-purple">
              <UsersIcon width={24} height={24} />
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-section">
        <h2 className="dashboard-section-title">Quick Actions</h2>
        <div className="dashboard-actions">
          <button
            className="dashboard-action-btn dashboard-action-btn-purple"
            onClick={onContractStart}
          >
            <DocumentIcon width={20} height={20} />
            Create Contract
          </button>
          <button
            className="dashboard-action-btn dashboard-action-btn-pink"
            onClick={handleUseTemplate}
          >
            <TemplatesIcon width={20} height={20} />
            Browse Templates
          </button>
          <button
            className="dashboard-action-btn dashboard-action-btn-cyan"
            onClick={handleCollabroter}
          >
            <ImportIcon width={20} height={20} />
            Import Collaborators
          </button>
          <button
            className="dashboard-action-btn dashboard-action-btn-green"
            onClick={handlePricingPlane}
          >
            <PlusIcon width={20} height={20} />
            <span className="fs-5 text-white">{totalEnvelope}</span>{" "}
            Contract Remaining
          </button>
        </div>
      </div>

      <div className="status-card">
        <h3 className="status-title">Contracts by Status</h3>

        <div className="status-labels">
          {statusData.map((s) => {
            const count = negotiationByStatus[s.key] ?? 0;
            const activeColor = count > 0 ? s.color : "#ffffffff";
            const StatusIcon = s.icon;
            
            // Convert hex to rgb for CSS variables
            const hexToRgb = (hex) => {
              const r = parseInt(hex.slice(1, 3), 16);
              const g = parseInt(hex.slice(3, 5), 16);
              const b = parseInt(hex.slice(5, 7), 16);
              return `${r}, ${g}, ${b}`;
            };
            
            return (
              <div 
                key={s.key} 
                className="status-label-item"
                style={{ 
                  "--status-color": activeColor,
                  "--status-color-rgb": hexToRgb(activeColor)
                }}
              >
                <div className="status-item-glow"></div>
                <div className="status-item-icon-wrapper">
                  <StatusIcon width={22} height={22} color={activeColor} />
                </div>
                <div className="status-item-content">
                  <h4 style={{ color: activeColor }}>
                    {count}
                  </h4>
                  <p>{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="activity-section">
        <div className="activity-container-compact">
          {/* MIDDLE: Recent Activity */}
          <div className="activity-recent-box">
            <div className="activity-card-compact">
              <h3 className="activity-subtitle-compact">
                Recent Activity
              </h3>

              {recentActivity?.length > 0 ? (
                <div className="activity-list-compact">
                  {recentActivity.map((activity, index) => {
                    return (
                      <div
                        key={index}
                        className="activity-item-compact"
                      >
                        <div className="activity-content-compact">
                          <p className="activity-message-compact">
                            {activity.message.split('"')[0]}
                            {activity.message && (
                              <span className="activity-highlight">
                                {
                                  activity.message.match(
                                    /"([^"]*)"/
                                  )?.[1]
                                }
                              </span>
                            )}
                          </p>
                          <p className="activity-time-compact">
                            {activity.time}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="activity-empty-compact">
                  <p>No recent activity</p>
                </div>
              )}
            </div>
          </div>

          {/* LEFT: Calendar */}
          <div className="activity-calendar-box">
            <div className="activity-card-compact">
              <h3 className="activity-subtitle-compact">Calendar</h3>
              <div className="calendar-responsive">
                <Calendar
                  value={selectedDate}
                  onChange={handleDateChange}
                  tileClassName={getTileClassName}
                />
              </div>
            </div>
          </div>

          {/* FULL WIDTH: Events List with Filter */}
          <div className="activity-card-compact activity-events-full">
            <div className="events-header">
              <h3 className="activity-subtitle-compact">
                {getFilterLabel()}
              </h3>

              {/* Custom Dropdown */}
              <div className="custom-dropdown-wrapper-guest">
                <button
                  className="custom-dropdown-button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  {
                    filterOptions.find(
                      (opt) => opt.value === eventFilter
                    )?.label
                  }
                  <span
                    className={`dashboard-dropdown-arrow ${
                      dropdownOpen ? "open" : ""
                    }`}
                  >
                    ▼
                  </span>
                </button>

                {dropdownOpen && (
                  <div className="custom-dropdown-menu">
                    {filterOptions.map((option) => (
                      <button
                        key={option.value}
                        className={`custom-dropdown-item ${
                          eventFilter === option.value
                            ? "active"
                            : ""
                        }`}
                        onClick={() => {
                          setEventFilter(option.value);
                          setDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {selectedDateEvents?.length > 0 ? (
              <div className="activity-list-events-full">
                {selectedDateEvents.map((event, index) => {
                  const eventDateObj = new Date(event.date);
                  const daysDiff = getDaysDifference(event.date);

                  return (
                    <div
                      key={index}
                      className="activity-item-event-full"
                    >
                      <div className="activity-content-event-full">
                        <p className="event-title-full">
                          {event.title}
                        </p>
                        <p className="event-date-full">
                          {eventDateObj.toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            }
                          )}
                          {daysDiff > 0 &&
                            ` - In ${daysDiff} day${
                              daysDiff !== 1 ? "s" : ""
                            }`}
                          {daysDiff === 0 && " - Today"}
                          {daysDiff < 0 &&
                            ` - ${Math.abs(
                              daysDiff
                            )} day${
                              Math.abs(daysDiff) !== 1 ? "s" : ""
                            } ago`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="activity-empty-compact">
                <p>No events found for this filter</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
